import { useCallback, useEffect, useMemo, useState } from 'react';
import api from 'services/api';
import {
  ProdutoAfiliado,
  ProdutoAfiliadoBaseInput,
  atualizarProdutoAfiliado,
  criarProdutoAfiliado,
  importarCatalogoProdutosAfiliados,
  listarProdutosAfiliadosAdmin,
  removerProdutoAfiliado,
} from 'services/produtosAfiliados';

import {
  FiltrosForm,
  ListaTemplateResumo,
  ProdutoFormState,
} from './AdminProdutosAfiliadosPage.types';

const defaultFilters: FiltrosForm = {
  search: '',
  categoria: '',
  parceiro: '',
  templateSlug: '',
  ativo: 'todos',
  destaque: 'todos',
  minPreco: '',
  maxPreco: '',
};

const defaultFormState: ProdutoFormState = {
  nome: '',
  descricao: '',
  preco: '',
  imagemUrl: '',
  parceiro: '',
  urlAfiliado: '',
  categoria: '',
  tagsRaw: '',
  ativo: true,
  destaque: false,
  origemDados: '',
  externalId: '',
  estoqueDisponivel: true,
  templateSlugs: [],
  ordemSugestao: '',
};

const formatarMoeda = (valor: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor);

export const useAdminProdutosAfiliadosRules = () => {
  const [templates, setTemplates] = useState<ListaTemplateResumo[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState<FiltrosForm>(defaultFilters);
  const [filters, setFilters] = useState<FiltrosForm>(defaultFilters);

  const [produtos, setProdutos] = useState<ProdutoAfiliado[]>([]);
  const [loadingProdutos, setLoadingProdutos] = useState(true);
  const [erro, setErro] = useState('');
  const [feedback, setFeedback] = useState('');
  const [importandoCatalogo, setImportandoCatalogo] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalProdutos, setTotalProdutos] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [categoriasRapidas, setCategoriasRapidas] = useState<string[]>([]);
  const [faixaPreco, setFaixaPreco] = useState<{ min: number | null; max: number | null } | null>(
    null
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState<ProdutoAfiliado | null>(null);
  const [formState, setFormState] = useState<ProdutoFormState>(defaultFormState);

  const categoriaAtiva = appliedFilters.categoria.trim();
  const faixaPrecoAtivaChave = `${appliedFilters.minPreco.trim()}|${appliedFilters.maxPreco.trim()}`;
  const parceiroAtivo = appliedFilters.parceiro.trim();
  const paginaNormalizada = totalPaginas > 0 ? Math.min(page, totalPaginas - 1) : 0;

  const filtrosAplicadosResumo = useMemo(() => {
    const resumo: string[] = [];
    if (appliedFilters.search) resumo.push(`Busca: "${appliedFilters.search}"`);
    if (appliedFilters.categoria) resumo.push(`Categoria: ${appliedFilters.categoria}`);
    if (appliedFilters.parceiro) resumo.push(`Parceiro: ${appliedFilters.parceiro}`);
    if (appliedFilters.templateSlug) resumo.push(`Template: ${appliedFilters.templateSlug}`);
    if (appliedFilters.ativo !== 'todos')
      resumo.push(`Ativo: ${appliedFilters.ativo === 'true' ? 'Sim' : 'Não'}`);
    if (appliedFilters.destaque !== 'todos')
      resumo.push(`Destaque: ${appliedFilters.destaque === 'true' ? 'Sim' : 'Não'}`);
    if (appliedFilters.minPreco) {
      const valor = Number.parseFloat(appliedFilters.minPreco.replace(',', '.'));
      if (!Number.isNaN(valor)) {
        resumo.push(`Preço min: ${formatarMoeda(valor)}`);
      }
    }
    if (appliedFilters.maxPreco) {
      const valor = Number.parseFloat(appliedFilters.maxPreco.replace(',', '.'));
      if (!Number.isNaN(valor)) {
        resumo.push(`Preço máx: ${formatarMoeda(valor)}`);
      }
    }
    return resumo.length ? resumo.join(' • ') : 'Nenhum filtro aplicado';
  }, [appliedFilters]);

  const carregarTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.get<{ templates: ListaTemplateResumo[] }>('/listas/templates');
      setTemplates(response.data.templates ?? []);
    } catch (error) {
      console.error('Erro ao carregar templates', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const carregarProdutos = useCallback(async () => {
    try {
      setLoadingProdutos(true);
      setErro('');

      const minPrecoStr = appliedFilters.minPreco.trim();
      const maxPrecoStr = appliedFilters.maxPreco.trim();
      const minPrecoNumber = minPrecoStr
        ? Number.parseFloat(minPrecoStr.replace(',', '.'))
        : undefined;
      const maxPrecoNumber = maxPrecoStr
        ? Number.parseFloat(maxPrecoStr.replace(',', '.'))
        : undefined;

      const filtros = {
        search: appliedFilters.search || undefined,
        categoria: appliedFilters.categoria || undefined,
        parceiro: appliedFilters.parceiro || undefined,
        templateSlug: appliedFilters.templateSlug || undefined,
        ativo: appliedFilters.ativo !== 'todos' ? appliedFilters.ativo === 'true' : undefined,
        destaque:
          appliedFilters.destaque !== 'todos' ? appliedFilters.destaque === 'true' : undefined,
        minPreco:
          typeof minPrecoNumber === 'number' && !Number.isNaN(minPrecoNumber)
            ? minPrecoNumber
            : undefined,
        maxPreco:
          typeof maxPrecoNumber === 'number' && !Number.isNaN(maxPrecoNumber)
            ? maxPrecoNumber
            : undefined,
        page: page + 1,
        pageSize,
      };

      const resposta = await listarProdutosAfiliadosAdmin(filtros);
      setProdutos(resposta.produtos);
      setTotalProdutos(resposta.total);
      setTotalPaginas(resposta.totalPages ?? 0);
      setFaixaPreco(resposta.faixaPreco ?? null);

      setCategoriasRapidas((prev) => {
        const merged = new Set(prev);
        (resposta.categoriasDisponiveis ?? []).forEach((c) => merged.add(c));
        if (appliedFilters.categoria) merged.add(appliedFilters.categoria);
        return Array.from(merged).sort((a, b) => a.localeCompare(b));
      });

      const paginaResposta = Math.max((resposta.page ?? 1) - 1, 0);
      if (paginaResposta !== page) {
        setPage(paginaResposta);
      }
      if (resposta.pageSize && resposta.pageSize !== pageSize) {
        setPageSize(resposta.pageSize);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error || 'Erro ao carregar catálogo de produtos afiliados';
      setErro(message);
    } finally {
      setLoadingProdutos(false);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    carregarTemplates();
  }, []);

  useEffect(() => {
    void carregarProdutos();
  }, [carregarProdutos]);

  const atualizarFiltro = (campo: keyof FiltrosForm, valor: string) => {
    setFilters((prev) => ({ ...prev, [campo]: valor }));
  };

  const handlePrecoFiltroChange = (campo: 'minPreco' | 'maxPreco', valor: string) => {
    const sanitizado = valor.replace(/[^0-9.,]/g, '');
    atualizarFiltro(campo, sanitizado);
  };

  const aplicarFiltros = () => {
    setPage(0);
    setAppliedFilters(filters);
  };

  const limparFiltros = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(0);
    setPageSize(25);
  };

  const dispararImportacaoCatalogo = useCallback(async () => {
    const confirmar = window.confirm(
      'Deseja atualizar o catálogo automaticamente? Isso pode levar alguns instantes enquanto buscamos novos produtos.'
    );

    if (!confirmar) return;

    try {
      setImportandoCatalogo(true);
      setErro('');
      setFeedback('');

      const resposta = await importarCatalogoProdutosAfiliados({ fornecedores: ['magalu'] });
      const magaluResumo = resposta.resultados.magalu;

      let resumoMensagem = resposta.message;
      if (magaluResumo) {
        resumoMensagem = `Magalu: ${magaluResumo.totalColetados} coletados (${magaluResumo.criados} novos, ${magaluResumo.atualizados} atualizados).`;
      } else if (resposta.resultados.amazon?.message) {
        resumoMensagem = resposta.resultados.amazon.message ?? resumoMensagem;
      }

      setFeedback(`Catálogo atualizado. ${resumoMensagem}`);
      await carregarProdutos();
    } catch (error: any) {
      const message =
        error?.response?.data?.error || 'Erro ao importar catálogo de produtos afiliados.';
      setErro(message);
    } finally {
      setImportandoCatalogo(false);
    }
  }, [carregarProdutos]);

  const aplicarCategoriaRapida = (categoriaSelecionada: string) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, categoria: categoriaSelecionada }));
    setAppliedFilters((prev) => ({ ...prev, categoria: categoriaSelecionada }));
  };

  const aplicarFaixaPrecoRapida = (min?: number, max?: number) => {
    const minStr = typeof min === 'number' ? min.toFixed(2) : '';
    const maxStr = typeof max === 'number' ? max.toFixed(2) : '';
    setPage(0);
    setFilters((prev) => ({ ...prev, minPreco: minStr, maxPreco: maxStr }));
    setAppliedFilters((prev) => ({ ...prev, minPreco: minStr, maxPreco: maxStr }));
  };

  const aplicarParceiroRapido = (parceiroSelecionado: string) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, parceiro: parceiroSelecionado }));
    setAppliedFilters((prev) => ({ ...prev, parceiro: parceiroSelecionado }));
  };

  const abrirDialogCriar = () => {
    setProdutoEditando(null);
    setFormState(defaultFormState);
    setDialogOpen(true);
    setFeedback('');
    setErro('');
  };

  const abrirDialogEditar = (produto: ProdutoAfiliado) => {
    setProdutoEditando(produto);
    setFormState({
      nome: produto.nome,
      descricao: produto.descricao ?? '',
      preco: String(produto.preco),
      imagemUrl: produto.imagemUrl ?? '',
      parceiro: produto.parceiro ?? '',
      urlAfiliado: produto.urlAfiliado,
      categoria: produto.categoria ?? '',
      tagsRaw: produto.tags.join(', '),
      ativo: produto.ativo,
      destaque: produto.destaque,
      origemDados: produto.origemDados ?? '',
      externalId: produto.externalId ?? '',
      estoqueDisponivel: produto.estoqueDisponivel ?? true,
      templateSlugs: produto.templatesSugestoes.map((item) => item.templateSlug),
      ordemSugestao: '',
    });
    setDialogOpen(true);
    setFeedback('');
    setErro('');
  };

  const fecharDialog = () => {
    if (salvando) return;
    setDialogOpen(false);
    setProdutoEditando(null);
  };

  const atualizarForm = (campo: keyof ProdutoFormState, valor: string | boolean | string[]) => {
    setFormState((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const montarPayload = (): ProdutoAfiliadoBaseInput | null => {
    const nome = formState.nome.trim();
    const urlAfiliado = formState.urlAfiliado.trim();
    const preco = Number(formState.preco);

    if (!nome || !urlAfiliado || Number.isNaN(preco) || preco <= 0) {
      setErro('Informe nome, URL do afiliado e um preço válido.');
      return null;
    }

    const payload: ProdutoAfiliadoBaseInput = {
      nome,
      urlAfiliado,
      preco,
      descricao: formState.descricao.trim() || undefined,
      imagemUrl: formState.imagemUrl.trim() || undefined,
      parceiro: formState.parceiro.trim() || undefined,
      categoria: formState.categoria.trim() || undefined,
      origemDados: formState.origemDados.trim() || undefined,
      externalId: formState.externalId.trim() || undefined,
      tags: formState.tagsRaw
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      ativo: formState.ativo,
      destaque: formState.destaque,
      estoqueDisponivel: formState.estoqueDisponivel,
      templateSlugs: formState.templateSlugs,
      ordemSugestao: formState.ordemSugestao ? Number(formState.ordemSugestao) : undefined,
    };

    if (!payload.tags?.length) {
      delete payload.tags;
    }

    if (!payload.templateSlugs?.length) {
      delete payload.templateSlugs;
      delete payload.ordemSugestao;
    }

    return payload;
  };

  const salvarProduto = async () => {
    const payload = montarPayload();
    if (!payload) return;

    try {
      setSalvando(true);
      setErro('');
      setFeedback('');

      if (produtoEditando) {
        await atualizarProdutoAfiliado(produtoEditando.id, payload);
        setFeedback('Produto atualizado com sucesso.');
      } else {
        await criarProdutoAfiliado(payload);
        setFeedback('Produto criado com sucesso.');
      }

      setDialogOpen(false);
      await carregarProdutos();
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Não foi possível salvar o produto.';
      setErro(message);
    } finally {
      setSalvando(false);
    }
  };

  const excluirProduto = async (produto: ProdutoAfiliado) => {
    const confirmar = window.confirm(
      `Remover o produto "${produto.nome}"? Ele deixará de aparecer no catálogo, mas poderá ser reativado depois.`
    );
    if (!confirmar) return;

    try {
      await removerProdutoAfiliado(produto.id);
      setFeedback('Produto removido com sucesso.');
      await carregarProdutos();
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Erro ao remover produto.';
      setErro(message);
    }
  };

  const templatesDisponiveis = useMemo(
    () => [...templates].sort((a, b) => a.titulo.localeCompare(b.titulo)),
    [templates]
  );

  const precoChips = useMemo(
    () => [
      { label: `Até ${formatarMoeda(80)}`, max: 80 },
      { label: `${formatarMoeda(80)} - ${formatarMoeda(200)}`, min: 80, max: 200 },
      { label: `${formatarMoeda(200)} - ${formatarMoeda(500)}`, min: 200, max: 500 },
      { label: `${formatarMoeda(500)} - ${formatarMoeda(800)}`, min: 500, max: 800 },
      { label: `${formatarMoeda(800)} - ${formatarMoeda(1200)}`, min: 800, max: 1200 },
      { label: `Acima de ${formatarMoeda(1200)}`, min: 1200, max: undefined },
    ],
    []
  );

  return {
    // dados
    templates,
    loadingTemplates,
    appliedFilters,
    filters,
    produtos,
    loadingProdutos,
    erro,
    feedback,
    importandoCatalogo,
    page,
    pageSize,
    totalProdutos,
    totalPaginas,
    categoriasRapidas,
    faixaPreco,
    categoriaAtiva,
    parceiroAtivo,
    faixaPrecoAtivaChave,
    paginaNormalizada,
    dialogOpen,
    salvando,
    produtoEditando,
    formState,
    filtrosAplicadosResumo,
    templatesDisponiveis,
    precoChips,
    // handlers
    setPage,
    setPageSize,
    atualizarFiltro,
    handlePrecoFiltroChange,
    aplicarFiltros,
    limparFiltros,
    dispararImportacaoCatalogo,
    aplicarCategoriaRapida,
    aplicarFaixaPrecoRapida,
    aplicarParceiroRapido,
    abrirDialogCriar,
    abrirDialogEditar,
    fecharDialog,
    atualizarForm,
    salvarProduto,
    excluirProduto,
    setFilters,
    setAppliedFilters,
  };
};

export type UseAdminProdutosAfiliadosRulesReturn = ReturnType<
  typeof useAdminProdutosAfiliadosRules
>;
